import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { qk, fetchClients, createStrategy } from '@/lib/queries';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import NumberInput from '@/components/ui/NumberInput';
import { CREATIVE_TYPES, type CreativeType } from '@shared/enums';

export default function StrategyCreatePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();

  const clients = useQuery({ queryKey: qk.clients, queryFn: fetchClients });

  const [clientId, setClientId] = useState(params.get('clientId') ?? '');
  const [title, setTitle] = useState('');
  const [talentId, setTalentId] = useState('');
  const [description, setDescription] = useState('');
  const [start, setStart] = useState(today());
  const [end, setEnd] = useState(addDays(today(), 30));
  const [tof, setTof] = useState(5);
  const [mof, setMof] = useState(3);
  const [bof, setBof] = useState(2);
  const [mix, setMix] = useState<Partial<Record<CreativeType, number>>>({ video: 2, reel: 3, static: 3, carousel: 2 });

  useEffect(() => {
    if (!clientId && clients.data && clients.data.length > 0) {
      setClientId(clients.data[0].id);
    }
  }, [clients.data, clientId]);

  const totalCreatives = tof + mof + bof;
  const totalMix = Object.values(mix).reduce<number>((a, b) => a + (b ?? 0), 0);

  const create = useMutation({
    mutationFn: async () => {
      if (!clientId) throw new Error('Pick a client');
      if (!title.trim()) throw new Error('Title required');
      if (totalCreatives !== totalMix) {
        throw new Error(`Funnel total (${totalCreatives}) must match creative-mix total (${totalMix})`);
      }
      return createStrategy({
        client_id: clientId,
        title: title.trim(),
        description: description.trim() || undefined,
        period_start: start,
        period_end: end,
        funnel_mix: { TOF: tof, MOF: mof, BOF: bof },
        creative_mix: mix as Record<string, number>,
        talent_id: talentId || undefined,
      });
    },
    onSuccess: (s) => {
      toast.success(`Strategy ${s.code} created`);
      qc.invalidateQueries({ queryKey: qk.strategies });
      navigate(`/strategies/${s.id}`);
    },
  });

  return (
    <div className="max-w-3xl">
      <PageHeader title="New strategy" subtitle="The Marketing Strategist's masterpiece - drives the whole production cycle" />

      <Card className="space-y-5">
        <Select
          label="Client"
          value={clientId}
          onChange={setClientId}
          searchable
          placeholder="Select a client..."
          options={(clients.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
        />

        {((clients.data ?? []).find((c) => c.id === clientId)?.talents ?? []).length > 0 && (
          <Select label="For a doctor (optional)" value={talentId} onChange={setTalentId} placeholder="No doctor"
            options={[{ value: '', label: 'No doctor' }, ...((clients.data ?? []).find((c) => c.id === clientId)?.talents ?? []).map((t) => ({ value: t.id, label: t.name }))]} />
        )}

        <Input label="Strategy title" placeholder="e.g. Summer 2026 Awareness Push" value={title} onChange={(e) => setTitle(e.target.value)} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Period start" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          <Input label="Period end"   type="date" value={end}   onChange={(e) => setEnd(e.target.value)} />
        </div>

        <Input label="Description / objective" placeholder="Why does this strategy exist?" value={description} onChange={(e) => setDescription(e.target.value)} />

        <div className="space-y-2 pt-2 border-t border-slate-800">
          <div className="text-xs text-muted">Funnel mix (count of creatives)</div>
          <div className="grid grid-cols-3 gap-3">
            <NumberCount label="TOF (top of funnel)" value={tof} onChange={setTof} />
            <NumberCount label="MOF (middle)"         value={mof} onChange={setMof} />
            <NumberCount label="BOF (bottom)"         value={bof} onChange={setBof} />
          </div>
          <div className="text-xs text-muted text-right">Total: {totalCreatives}</div>
        </div>

        <div className="space-y-2 pt-2 border-t border-slate-800">
          <div className="text-xs text-muted">Creative mix (must total {totalCreatives})</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {CREATIVE_TYPES.map((t) => (
              <NumberCount key={t} label={t} value={mix[t] ?? 0} onChange={(v) => setMix({ ...mix, [t]: v })} />
            ))}
          </div>
          <div className={'text-xs text-right ' + (totalMix === totalCreatives ? 'text-muted' : 'text-yellow-400')}>
            Total: {totalMix} / {totalCreatives}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => navigate('/strategies')}>Cancel</Button>
          <Button onClick={() => create.mutate()} loading={create.isPending}>Create strategy</Button>
        </div>
      </Card>
    </div>
  );
}

function NumberCount({ label, value, onChange }: { label: string; value: number; onChange: (n: number) => void }) {
  return (
    <div>
      <div className="text-[10px] text-muted uppercase tracking-wide mb-1">{label}</div>
      <NumberInput value={value} onChange={onChange} min={0} size="sm" />
    </div>
  );
}

function today(): string { return new Date().toISOString().slice(0, 10); }
function addDays(iso: string, days: number): string {
  const d = new Date(iso); d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
